import { useEffect, useRef, useState } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, {
  TableHeading,
  TableControls,
  TableFooter,
  buildTwoLineColumn,
  buildActionColumn,
  type Row,
  type TableAction
} from "@/components/Table";
import AppLayout from "@/layouts/AppLayout";
import EmptyState from "@/components/EmptyState";
import { useApolloClient, useQuery } from "@apollo/client";
import {
  EVENTS_QUERY,
  EVENTS_SEARCH_INPUT_FIELDS_QUERY,
  GET_EVENT_TYPES,
  isEventCodeSearchSupported,
  UNIVERSITY_EVENTS_QUERY
} from "./queries";
import { EventType } from "./types";
import { EventsData, Event, EventsQueryVariables } from "./types";
import { toDateFormat } from "@/lib/dateHelpers";
import { buildMyPgaUrl } from "@/lib/urlUtils";
import {
  formatEventListDate,
  formatEventRegistrations,
  getEventListEndsAt,
  getEventListStartsAt,
  readCachedEventCodeSearchSupport,
  renderEventStatus,
  shouldResetEventsListPagination,
  writeCachedEventCodeSearchSupport
} from "./_utils";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { snakeCase } from "lodash";
import SearchInput from "@/components/SearchInput";
import DateSelect from "@/components/DateSelect";
import { useNavigate, Path } from "@/router";
import MultiSelect from "@/components/MultiSelect";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { getEventTypeName } from "./[eventSlug]/utils";
import { ColumnSort } from "@tanstack/react-table";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import CancelEventModal, { CancelEvent } from "./[eventSlug]/(tabbed)/cancellations/_components/CancelEventModal";
import usePagination from "@/hooks/usePagination";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import { useEventsBasePath } from "./_eventsBasePathContext";
import { useParams as useRouteParams } from "react-router-dom";

const DEFAULT_STATUS_VALUES = ["Active", "Pending"];

export type EventsListPageProps = {
  /** When set, results are scoped to events managed by this university (UUID) and the list renders without the top-level Events AppLayout shell. */
  embeddedUniversityId?: string;
};

export function EventsListPage({ embeddedUniversityId }: EventsListPageProps = {}) {
  const basePath = useEventsBasePath();
  const { universityId: universityLegacyId } = useRouteParams();
  usePageTitle(embeddedUniversityId ? "/education/universities/:universityId/events" : basePath, "Events");
  let { params, searchParams, searchParamsUrl, addSearchParam, deleteSearchParams } = useParams();
  if (params.dateRange && !embeddedUniversityId) {
    params.dateRange = params.dateRange && {
      from: params.dateRange.split(",")[0],
      to: params.dateRange.split(",")[1]
    };
  }

  const [initialLoad, setInitialLoad] = useState(!embeddedUniversityId);
  const [supportsEventCodeSearch, setSupportsEventCodeSearch] = useState(false);
  const apolloClient = useApolloClient();

  const { hasPermission } = useCapabilities();
  const flags = useFlags();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "title"
  });

  const hasStatusParam = searchParams.has("status[]");

  useEffect(() => {
    if (!embeddedUniversityId && !hasStatusParam) {
      addSearchParam("status[]", DEFAULT_STATUS_VALUES.join(","));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE === "test") {
      setSupportsEventCodeSearch(readCachedEventCodeSearchSupport());
      return;
    }

    if (readCachedEventCodeSearchSupport()) {
      setSupportsEventCodeSearch(true);
      return;
    }

    apolloClient
      .query({ query: EVENTS_SEARCH_INPUT_FIELDS_QUERY, fetchPolicy: "network-only" })
      .then(({ data }) => {
        const supported = isEventCodeSearchSupported(data);
        writeCachedEventCodeSearchSupport(supported);
        setSupportsEventCodeSearch(supported);
      })
      .catch(() => {
        writeCachedEventCodeSearchSupport(false);
        setSupportsEventCodeSearch(false);
      });
  }, [apolloClient]);

  // Build initial query from known keys only
  const initialQuery = {
    ...(!embeddedUniversityId && params.title && { title: params.title }),
    ...(!embeddedUniversityId && params.dateRange && { dateRange: params.dateRange }),
    ...(!embeddedUniversityId && params.status && { status: params.status })
  } as EventsQueryVariables["query"];
  const [variables, setVariables] = useState<EventsQueryVariables>({
    query: initialQuery,
    eventTypes:
      embeddedUniversityId || !Array.isArray(params.eventType) || params.eventType.length === 0
        ? undefined
        : params.eventType,
    ...(embeddedUniversityId && {
      orderBy: {
        field: "UPDATED_AT",
        direction: "DESC"
      }
    })
  });
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();

  const defaultStatusValues = embeddedUniversityId ? [] : params.status || DEFAULT_STATUS_VALUES;
  const defaultEventTypeValues = embeddedUniversityId ? [] : params.eventType || [];

  const [selectedValues, setSelectedValues] = useState<string[]>(defaultStatusValues);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(defaultEventTypeValues);

  useEffect(() => {
    if (!embeddedUniversityId) return;

    deleteSearchParams(["title", "eventType[]", "status[]", "dateRange", "after", "before", "currentPage"]);
  }, [embeddedUniversityId, deleteSearchParams]);

  const filterState = {
    search: searchValue.trim().length > 0,
    status: selectedValues.length > 0,
    eventType: selectedEventTypes.length > 0,
    dates: !!(variables.query?.dateRange?.from && variables.query?.dateRange?.to)
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  const handleClearAllFilters = () => {
    setVariables({ query: {}, eventTypes: [] });
    setSelectedValues([]);
    setSelectedEventTypes([]);
    clearSearch();
    if (!embeddedUniversityId) {
      deleteSearchParams(["dateRange", "status[]", "eventType[]", "after", "before", "currentPage"]);
    }
    resetPagination();
  };

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [isFacilitySideSheetOpen, setIsFacilitySideSheetOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);

  const { Menu, setMenu } = useTableRowMenu();
  const navigate = useNavigate();

  // Fetch event types for filter
  const { data: eventTypesData } = useQuery<{ eventTypes: EventType[] }>(GET_EVENT_TYPES, {
    skip: !!embeddedUniversityId
  });

  function navigateToEvent(eventSlug: string) {
    const eventPath = `${basePath}/${eventSlug}?return_to=${basePath}&${searchParamsUrl}`;
    navigate(eventPath as Path);
  }

  function onClickEdit(row: Row<Event>) {
    const editPath = `${basePath}/${row.original.slug}/edit?return_to=${basePath}&${searchParamsUrl}`;
    navigate(editPath as Path, {
      state: { event: row.original }
    });
    window.scrollTo(0, 0);
  }

  function onClickDuplicate(row: Row<Event>) {
    navigate(`${basePath}/new` as Path, {
      state: { event: row.original }
    });
    window.scrollTo(0, 0);
  }

  function onClickCancel(row: Row<Event>) {
    setSelectedEvent(row.original);
    setIsCancelModalOpen(true);
  }

  const [orderBy, setOrderBy] = useState<ColumnSort[]>(() => [
    { id: embeddedUniversityId ? "updatedAt" : "startsAt", desc: true }
  ]);

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const prevOrderByRef = useRef(orderBy);
  const prevSupportsEventCodeSearchRef = useRef(supportsEventCodeSearch);

  useEffect(() => {
    const searchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
    const orderByChanged = prevOrderByRef.current !== orderBy;
    const supportChanged = prevSupportsEventCodeSearchRef.current !== supportsEventCodeSearch;

    if (
      shouldResetEventsListPagination({
        searchChanged,
        orderByChanged,
        supportChanged,
        debouncedSearch
      })
    ) {
      resetPagination();
    }

    prevDebouncedSearchRef.current = debouncedSearch;
    prevOrderByRef.current = orderBy;
    prevSupportsEventCodeSearchRef.current = supportsEventCodeSearch;

    const { id, desc } = orderBy[0] || {};
    setVariables((prevVariables) => ({
      query: {
        ...prevVariables.query,
        title: debouncedSearch || undefined,
        ...(supportsEventCodeSearch && debouncedSearch ? { eventCode: debouncedSearch } : { eventCode: undefined })
      },
      ...(id && {
        orderBy: {
          field: snakeCase(id).toUpperCase(),
          direction: desc ? "DESC" : "ASC"
        }
      })
    }));
    setInitialLoad(false);
  }, [debouncedSearch, orderBy, resetPagination, setInitialLoad, supportsEventCodeSearch]);

  const eventsQuery = embeddedUniversityId ? UNIVERSITY_EVENTS_QUERY : EVENTS_QUERY;

  const { data, loading, refetch } = useQuery<EventsData>(eventsQuery, {
    variables: {
      ...variables,
      ...paginationVariables,
      query: {
        ...variables.query,
        status: selectedValues.length > 0 ? selectedValues : undefined,
        ...(embeddedUniversityId ? { universityId: embeddedUniversityId } : {})
      },
      eventTypes: selectedEventTypes.length > 0 ? selectedEventTypes : undefined
    },
    skip: initialLoad
  });

  const eventData = data?.events?.edges?.map(({ node: event }) => ({
    ...event,
    updatedAt: toDateFormat(event.updatedAt) || ""
  }));

  const handleCreateEventClick = () => {
    if (embeddedUniversityId) {
      const legacyIdForRoute = universityLegacyId ?? embeddedUniversityId;
      const returnToPath = `/education/universities/${legacyIdForRoute}/events`;
      navigate(
        `/events/new?return_to=${encodeURIComponent(returnToPath)}&universityId=${encodeURIComponent(embeddedUniversityId)}` as Path,
        {
          state: { universityId: embeddedUniversityId }
        }
      );
      return;
    }

    navigate(`${basePath}/new` as Path);
  };

  const isStandalone = basePath === "/events" && !embeddedUniversityId;
  const headingActions: TableAction[] = hasPermission(PERMISSION.manageEvents)
    ? [
        {
          label: "Create Event",
          icon: "add" as const,
          onClick: handleCreateEventClick,
          testId: "create-event-button"
        }
      ]
    : [];
  const pageActions: Action[] = [
    ...(hasPermission(PERMISSION.manageEvents)
      ? [
          {
            text: "Create Event",
            iconName: "add" as const,
            onClick: handleCreateEventClick,
            testId: "create-event-button"
          }
        ]
      : []),
    ...(isStandalone && hasPermission(PERMISSION.editPAT) && flags["backoffice-pat-result-volume"] === true
      ? [
          {
            text: "PAT Results",
            iconName: "bar_chart" as const,
            variant: "outlined" as const,
            onClick: () => navigate("/events/pat-results"),
            testId: "pat-results-button"
          }
        ]
      : [])
  ];

  const handleSort = (updaterOrValue: any) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  const nameColumn = buildTwoLineColumn<Event>({
    accessorKey: "title",
    header: "Event Name",
    getPrimaryText: (row) => row.title,
    getSecondaryText: (row) => getEventTypeName(row) ?? "",
    secondaryLabel: "Type",
    size: 500,
    minSize: 280,
    sortType: "text"
  });

  const content = (
    <>
      <Menu />
      {embeddedUniversityId ? (
        <TableHeading
          heading="PGM University Events"
          body="All events for this PGM University can be viewed and managed below."
          actions={headingActions}
        />
      ) : (
        <div className="pt-12" />
      )}
      <Table
        data={eventData || []}
        loading={loading}
        hasActiveFilters={!!searchValue || hasActiveFilters}
        getRowLink={(row) => ({
          to: `${embeddedUniversityId ? `/education/universities/${universityLegacyId}/events/${row.original.slug}/registrations` : `${basePath}/${row.original.slug}?return_to=${basePath}&${searchParamsUrl}`}` as any
        })}
        onSortingChange={handleSort}
        sortingState={orderBy}
        serverSideSorting={true}
        renderNoDataEmptyState={() => <EmptyState title="No Events" caption="No events exist yet." iconName="event" />}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="search" />
        )}
        renderControls={(_table, { onSortClick }) => (
          <TableControls
            pagination={createHeaderPagination(data?.events, {
              pageCount: eventData?.length,
              loading
            })}
            actions={embeddedUniversityId ? [] : pageActions}
            onSortClick={onSortClick}
            useExpandedFilters={true}
            expandedFiltersState={filterState}
            onClearAllFilters={handleClearAllFilters}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Events"
              query={searchValue}
              onClear={clearSearch}
              onChange={setSearchValue}
            />
            <div data-name="eventType">
              <MultiSelect
                label="Event Type"
                selectedValues={selectedEventTypes}
                options={
                  eventTypesData?.eventTypes?.map((eventType) => ({
                    value: eventType.name,
                    label: eventType.name
                  })) || []
                }
                onSelect={(value, checked) => {
                  const eventTypes = checked
                    ? [...selectedEventTypes, value]
                    : selectedEventTypes.filter((v) => v !== value);
                  setSelectedEventTypes(eventTypes);
                  setVariables((prevVariables) => ({
                    ...prevVariables,
                    eventTypes: eventTypes.length > 0 ? eventTypes : undefined
                  }));
                  addSearchParam("eventType[]", eventTypes.join(","));
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedEventTypes([]);
                  setVariables((prevVariables) => ({
                    ...prevVariables,
                    eventTypes: undefined
                  }));
                  deleteSearchParams("eventType[]");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="status">
              <MultiSelect
                label="Stage"
                selectedValues={selectedValues}
                options={[
                  { value: "Active", label: "Active" },
                  { value: "Pending", label: "Pending" },
                  { value: "Cancelled", label: "Canceled" },
                  { value: "Completed", label: "Completed" },
                  { value: "Draft", label: "Draft" }
                ]}
                onSelect={(value, checked) => {
                  const status = checked ? [...selectedValues, value] : selectedValues.filter((v) => v !== value);
                  setSelectedValues(status);
                  setVariables((prevVariables) => ({
                    ...prevVariables,
                    query: {
                      ...prevVariables.query,
                      status
                    }
                  }));
                  addSearchParam("status[]", status.join(","));
                  resetPagination();
                }}
                onClear={() => {
                  setSelectedValues([]);
                  deleteSearchParams("status[]");
                  resetPagination();
                }}
              />
            </div>
            <div data-name="dates" data-filter-size="custom">
              <DateSelect
                label="Session Date Range"
                placeholder={embeddedUniversityId ? "All Session Dates" : "Show All"}
                useModal={true}
                useDefaultShortcuts={true}
                value={
                  embeddedUniversityId
                    ? {
                        startDate: variables.query?.dateRange?.from ?? null,
                        endDate: variables.query?.dateRange?.to ?? null
                      }
                    : params.dateRange
                      ? {
                          startDate: params.dateRange.from,
                          endDate: params.dateRange.to
                        }
                      : { startDate: null, endDate: null }
                }
                onChange={(e) => {
                  const { query } = variables;
                  if (typeof e === "object" && e !== null && e.startDate && e.endDate) {
                    setVariables({
                      query: {
                        ...query,
                        dateRange: { from: e.startDate, to: e.endDate }
                      }
                    });
                    if (!embeddedUniversityId) {
                      addSearchParam("dateRange", [e.startDate, e.endDate].join(","));
                    }
                  } else {
                    setVariables({
                      query: {
                        ...query,
                        dateRange: undefined
                      }
                    });
                    if (!embeddedUniversityId) {
                      deleteSearchParams("dateRange");
                    }
                  }
                  resetPagination();
                }}
              />
            </div>
          </TableControls>
        )}
        columns={[
          nameColumn,
          {
            accessorKey: "eventCode",
            header: "Event Code",
            cell: ({ row }) => row.original.eventCode?.trim() || "—",
            enableSorting: false
          },
          {
            accessorKey: "registrationCount",
            header: "Registrations",
            cell: ({ row }) => formatEventRegistrations(row.original),
            meta: { sortType: "numeric" }
          },
          {
            accessorKey: "updatedAt",
            header: "Modified",
            meta: { sortType: "date" }
          },
          {
            accessorKey: "startsAt",
            header: "Start Date",
            cell: ({ row }) => formatEventListDate(getEventListStartsAt(row.original), row.original.timezone),
            meta: { sortType: "date" }
          },
          {
            accessorKey: "endsAt",
            header: "End Date",
            cell: ({ row }) => formatEventListDate(getEventListEndsAt(row.original), row.original.timezone),
            meta: { sortType: "date" }
          },
          {
            accessorKey: "sessionCount",
            header: "Sessions",
            meta: { sortType: "count" }
          },
          {
            cell: (cellProps) => <>{renderEventStatus(cellProps.row.original.status)}</>,
            header: "Stage",
            id: "stage",
            meta: {
              headerClassName: "hide-header-in-column"
            }
          },
          buildActionColumn<Event>({
            getActions: (row) => {
              const { manageEvents = false } = row.section.permissions || {};
              const eventStatus = row.status;
              const isDraft = eventStatus === "Draft";
              const eventType = getEventTypeName(row) ?? "";
              const isRecruitment = eventType === "Recruitment";
              const canViewEvent = manageEvents && (eventStatus === "Canceled" || eventStatus === "Completed");
              const canManageEvent = manageEvents;

              const manageEventAction: Action = {
                text: "Manage Event",
                section: "Manage",
                onClick: () => navigateToEvent(row.slug)
              };

              const viewEventAction: Action = {
                text: "View Event",
                section: "Manage",
                onClick: () => navigateToEvent(row.slug)
              };

              const editAction: Action = {
                text: "Edit Event",
                section: "Manage",
                onClick: () => onClickEdit({ original: row } as Row<Event>)
              };

              const duplicateAction: Action = {
                text: "Duplicate Event",
                section: "Actions",
                onClick: () => onClickDuplicate({ original: row } as Row<Event>)
              };

              const viewPublishedPageAction: Action = {
                text: isDraft ? "Preview Page" : "Published Page",
                iconName: "open_in_new",
                section: "Sharing",
                onClick: () => {
                  open(buildMyPgaUrl("member/events", row.slug), "_blank");
                }
              };

              const cancelAction: Action = {
                text: "Cancel Event",
                destructive: true,
                onClick: () => onClickCancel({ original: row } as Row<Event>)
              };

              if (canViewEvent) return [viewEventAction, duplicateAction];

              const actions: Action[] = [];

              if (!isDraft) actions.push(manageEventAction);
              if (canManageEvent) actions.push(editAction);
              if (canManageEvent || canViewEvent) actions.push(duplicateAction);
              if (!isRecruitment) actions.push(viewPublishedPageAction);
              if (canManageEvent) actions.push(cancelAction);

              return actions;
            },
            onViewClick: (row) => {
              if (row.facility) {
                setSelectedFacilityId(row.facility.id);
                setIsFacilitySideSheetOpen(true);
              }
            },
            showViewButton: (row) => !!row.facility,
            viewButtonTitle: "View Facility Details",
            actionId,
            setMenu,
            getRowId: (row) => row.id
          })
        ]}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(data?.events, {
              pageCount: eventData?.length,
              loading
            })}
          />
        )}
      />
      {isCancelModalOpen && (
        <CancelEventModal
          event={selectedEvent as unknown as CancelEvent}
          open={isCancelModalOpen}
          setOpen={(value) => {
            setIsCancelModalOpen(value);
            if (!value) {
              setSelectedEvent(null);
            }
          }}
          refetch={refetch}
        />
      )}
      <FacilitySideSheet
        open={isFacilitySideSheetOpen}
        onClose={() => {
          setIsFacilitySideSheetOpen(false);
          setSelectedFacilityId(null);
        }}
        facilityId={selectedFacilityId}
      />
    </>
  );
  return isStandalone ? <AppLayout>{content}</AppLayout> : content;
}

export default function EventPage() {
  return <EventsListPage />;
}
